// RDb: the sync root and orchestrator for a deployed database. Its DAG records
// deployment membership (advisory, monotonic, add-only); its runtime role is
// to ensure member objects and their transitive references (RSchemas, bound
// foreign groups) are present and syncing in the replica — this is where the
// loosely-specified startSync / stopSync of RObject earn their keep.
//
// ACTIONS (see payload.ts for formats):
//
//   create
//     Genesis of the sync root. Carries: seed, optional name, optional
//     creators (keyId + publicKey; deployment authority when non-empty),
//     hash algorithm.
//
//   add-schema
//     Records an RSchema as part of the deployment (monotonic, no removal in
//     v1; optional free-form `note`). When creators are declared, requires
//     author + signature from a creator.
//
//   add-group
//     Records a deployed RTableGroup as part of the deployment (monotonic;
//     optional free-form `note`). When creators are declared, requires
//     author + signature from a creator.
//
// Invariants:
//   - RDb state is ADVISORY: nothing's validity ever depends on it; groups are
//     fully valid and verifiable without their RDb. extractForeignDeps returns
//     undefined for this reason (membership never gates op validation).
//   - Membership is keyed by schema / group id. The optional `note` is a
//     free-form comment: never resolved, never a key. Name resolution for
//     qualified FK / exists / idProvider targets does NOT go through the RDb —
//     each RTableGroup fixes its own `bindings` (name -> group id) at creation.
//   - A member or referenced object missing from the replica triggers a mesh
//     fetch (ctx.fetchObject); if that is unavailable or fails, it is an
//     infrastructure error (throw), never an MVT data condition.
//
// startSync is two-step:
//   Step 1 (closure): BFS the transitive closure — members, each group's own
//     RSchema and bound foreign groups — fetching any object not yet present
//     with an explicit backend label. Backend labels are fixed at create /
//     fetch time and only memoized thereafter, so the whole closure must be
//     present before sessions open (resolveRefDag relies on a memoized label).
//   Step 2 (sessions): open one swarm + sync session per DAG in the closure
//     (including the RDb's own DAG) and activate it. stopSync tears them down.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var _a;
import { HASH_SHA256 } from "@hyper-hyper-space/hhs3_crypto";
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { version, formatValidationFailure, validationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { RootScopedDag, signPayload as signPayloadHelper, serializePublicKeyToBase64 } from "@hyper-hyper-space/hhs3_mvt";
import { createSyncSession } from "@hyper-hyper-space/hhs3_sync";
import { RDB_TYPE_ID } from "./payload.js";
import { validateRDbPayload } from "./validate_ops.js";
import { resolveMembers } from "./resolve.js";
import { RTABLE_GROUP_TYPE_ID } from "../rtable_group/group.js";
export { RDB_TYPE_ID } from "./payload.js";
export const rDbFactory = {
    computeRootObjectId: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const entry = dag.createEntry(payload, {}, position(), ctx.getCrypto().hash(HASH_SHA256));
        return entry.hash;
    }),
    validateCreationPayload: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
            return validationFailure("RDb create payload must be an object");
        }
        if (payload['action'] !== 'create')
            return validationFailure("RDb creation action must be 'create'");
        return validateRDbPayload(payload, { mode: 'create', ctx });
    }),
    executeCreationPayload: (payload, _ctx, scopedDag) => __awaiter(void 0, void 0, void 0, function* () {
        return yield scopedDag.append(payload, {}, position());
    }),
    loadObject: (id, ctx, opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        if ((opts === null || opts === void 0 ? void 0 : opts.parent) !== undefined) {
            throw new Error("RDb is a standalone object (no nesting parent)");
        }
        const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
        const rawDag = yield ctx.getDag(id, backendLabel);
        if (rawDag === undefined)
            throw new Error(`DAG '${id}' not found`);
        const scopedDag = new RootScopedDag(rawDag);
        const createOp = (yield scopedDag.loadEntry(id)).payload;
        return new RDbImpl(id, createOp, ctx, backendLabel);
    }),
};
export class RDbImpl {
    constructor(createOpId, createOp, ctx, backendLabel = 'default') {
        this.runtimeConfig = {};
        this.syncSessions = new Map();
        this.createOpId = createOpId;
        this.createOp = createOp;
        this.ctx = ctx;
        this.backendLabel = backendLabel;
    }
    getId() { return this.createOpId; }
    getType() { return _a.typeId; }
    getBackendLabel() { return this.backendLabel; }
    seed() { return this.createOp.seed; }
    hashAlgorithm() { return this.createOp.hashAlgorithm; }
    getCreators() {
        var _b;
        return [...((_b = this.createOp.creators) !== null && _b !== void 0 ? _b : [])];
    }
    isCreator(keyId) {
        return this.getCreators().some((c) => c.keyId === keyId);
    }
    getContext() { return this.ctx; }
    setRuntimeConfig(config) {
        this.runtimeConfig = Object.assign(Object.assign({}, this.runtimeConfig), config);
    }
    selfValidate() {
        return this.ctx.getConfig().selfValidate || false;
    }
    // --- Membership writers ---
    addSchema(schemaId, note, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const base = { action: 'add-schema', schemaId };
            if (note !== undefined)
                base.note = note;
            return this.applyMembership(base, author, at);
        });
    }
    addGroup(groupId, note, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const base = { action: 'add-group', groupId };
            if (note !== undefined)
                base.note = note;
            return this.applyMembership(base, author, at);
        });
    }
    applyMembership(base, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            let payload;
            if (this.getCreators().length > 0) {
                if (author === undefined) {
                    throw new Error(`RDb membership op '${base['action']}' requires an author when the database declares creators`);
                }
                payload = yield signPayloadHelper(base, author);
            }
            else {
                payload = base;
            }
            if (this.selfValidate()) {
                const result = yield this.validatePayload(payload, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return this.applyPayload(payload, at);
        });
    }
    // --- Membership resolution ---
    getMemberSchemas() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.resolveAt()).schemaIds;
        });
    }
    getMemberGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.resolveAt()).groupIds;
        });
    }
    resolveAt(at) {
        return __awaiter(this, void 0, void 0, function* () {
            var _b, e_1, _c, _d;
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            const entries = [];
            try {
                for (var _e = true, _f = __asyncValues(scopedDag.loadAllEntries()), _g; _g = yield _f.next(), _b = _g.done, !_b; _e = true) {
                    _d = _g.value;
                    _e = false;
                    const entry = _d;
                    entries.push(entry);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_e && !_b && (_c = _f.return)) yield _c.call(_f);
                }
                finally { if (e_1) throw e_1.error; }
            }
            return resolveMembers(entries, at);
        });
    }
    // --- RObject interface ---
    validatePayload(payload, _at) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
                return validationFailure("RDb membership payload must be an object", { objectHash: this.createOpId });
            }
            const action = payload['action'];
            // genesis-only action; never a valid post-creation op
            if (action !== 'add-schema' && action !== 'add-group') {
                return validationFailure(`action '${String(action)}' is not an RDb membership op`, { objectHash: this.createOpId });
            }
            return validateRDbPayload(payload, { mode: 'op', rdb: this });
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            return yield scopedDag.append(payload, {}, at);
        });
    }
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            from = from !== null && from !== void 0 ? from : yield scopedDag.getFrontier();
            const members = yield this.resolveAt(at);
            return new RDbView(this, at, from, [...members.schemaIds, ...members.groupIds]);
        });
    }
    // RDb membership is advisory: validity never depends on member presence, so
    // it declares no foreign deps (fetch is a runtime startSync concern).
    extractForeignDeps(_payload, _at) {
        return undefined;
    }
    computeDelta(_start, _end) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error("RDb is advisory; no delta in v1");
        });
    }
    createDeltaAccumulator(_start, _end) {
        throw new Error("RDb is advisory; no delta in v1");
    }
    subscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._scopedDag = new RootScopedDag(rawDag);
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._causalDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._causalDag = rawDag;
            }
            return this._causalDag;
        });
    }
    // --- SyncableObject: two-step fan-out ---
    startSync() {
        return __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            if (this.syncSessions.size > 0)
                return; // idempotent
            // Step 1: build the transitive closure, fetching anything missing.
            const closure = yield this.ensureClosurePresent();
            // Step 2: open one sync session per DAG in the closure.
            const mesh = this.ctx.getMesh((_b = this.runtimeConfig.meshLabel) !== null && _b !== void 0 ? _b : 'default');
            for (const id of closure) {
                if (this.syncSessions.has(id))
                    continue;
                const rObject = id === this.createOpId ? this : yield this.ctx.getObject(id);
                if (rObject === undefined)
                    throw new Error(`Object '${id}' vanished from the replica during startSync`);
                const label = id === this.createOpId
                    ? this.backendLabel
                    : (_c = (yield this.ctx.getBackendLabel(id))) !== null && _c !== void 0 ? _c : this.backendLabel;
                const rawDag = yield this.ctx.getDag(id, label);
                if (rawDag === undefined)
                    throw new Error(`DAG '${id}' not found during startSync`);
                const swarm = mesh.createSwarm(id);
                const target = {
                    dagId: id,
                    dag: rawDag,
                    rObject,
                    hashSuite: this.ctx.getHashSuite(),
                    resolveRefDag: (refId) => __awaiter(this, void 0, void 0, function* () { var _b; return this.ctx.getDag(refId, (_b = (yield this.ctx.getBackendLabel(refId))) !== null && _b !== void 0 ? _b : this.backendLabel); }),
                };
                const session = createSyncSession(target, [swarm]);
                swarm.activate();
                this.syncSessions.set(id, { swarm, session });
            }
        });
    }
    // BFS the closure of member ids + each group's schema + bound foreign
    // groups, fetching any object not yet present in the replica. Returns the
    // set of DAG ids to sync (including the RDb's own DAG).
    ensureClosurePresent() {
        return __awaiter(this, void 0, void 0, function* () {
            const members = yield this.resolveAt();
            const visited = new Set();
            const order = [];
            const queue = [this.createOpId, ...members.schemaIds, ...members.groupIds];
            while (queue.length > 0) {
                const id = queue.shift();
                if (visited.has(id))
                    continue;
                visited.add(id);
                order.push(id);
                if (id === this.createOpId)
                    continue; // RDb itself already present
                const obj = yield this.ensurePresent(id);
                // groups pull in their own schema and bound foreign groups
                if (obj.getType() === RTABLE_GROUP_TYPE_ID) {
                    const group = obj;
                    queue.push(group.getSchemaRef());
                    for (const boundId of Object.values(group.getBindings()))
                        queue.push(boundId);
                }
            }
            return order;
        });
    }
    // Return the object for `id`, fetching it from the mesh if it is not yet
    // present. A missing object with no fetch capability is an infra error.
    ensurePresent(id) {
        return __awaiter(this, void 0, void 0, function* () {
            var _b, _c;
            const existing = yield this.ctx.getObject(id);
            if (existing !== undefined)
                return existing;
            if (this.ctx.fetchObject === undefined) {
                throw new Error(`Object '${id}' is not present in the replica and the context cannot fetch it`);
            }
            try {
                return yield this.ctx.fetchObject(id, {
                    meshLabel: (_b = this.runtimeConfig.meshLabel) !== null && _b !== void 0 ? _b : 'default',
                    backendLabel: (_c = this.runtimeConfig.backendLabel) !== null && _c !== void 0 ? _c : this.backendLabel,
                    timeoutMs: this.runtimeConfig.fetchTimeoutMs,
                });
            }
            catch (err) {
                throw new Error(`Failed to fetch object '${id}' for RDb sync: ${err.message}`);
            }
        });
    }
    stopSync() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const { swarm, session } of this.syncSessions.values()) {
                session.destroy();
                swarm.destroy();
            }
            this.syncSessions.clear();
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.stopSync();
            this._scopedDag = undefined;
            this._causalDag = undefined;
        });
    }
}
_a = RDbImpl;
RDbImpl.create = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const createPayload = {
        action: 'create',
        type: RDB_TYPE_ID,
        seed: options.seed,
    };
    if (options.name !== undefined)
        createPayload.name = options.name;
    if (options.creators !== undefined && options.creators.length > 0) {
        createPayload.creators = options.creators.map((c) => ({
            keyId: c.keyId,
            publicKey: serializePublicKeyToBase64(c.publicKey),
        }));
    }
    if (options.hashAlgorithm !== undefined)
        createPayload.hashAlgorithm = options.hashAlgorithm;
    return createPayload;
});
RDbImpl.typeId = RDB_TYPE_ID;
// Minimal advisory membership view: members are the references; RDb pins no
// reference versions.
class RDbView {
    constructor(obj, at, from, members) {
        this.obj = obj;
        this.at = at;
        this.from = from;
        this.members = members;
    }
    getObject() { return this.obj; }
    getVersion() { return this.at; }
    getFromVersion() { return this.from; }
    getReferences() {
        return __awaiter(this, void 0, void 0, function* () { return [...this.members]; });
    }
    resolveRefVersion(_refId) {
        return __awaiter(this, void 0, void 0, function* () {
            return version(); // RDb does not pin observed versions
        });
    }
}
